import { usePersonField } from './PersonFields';
import { isValidElement } from 'react';
import {
  Banknote,
  BookOpen,
  Building2,
  Calendar,
  CircleDot,
  CreditCard,
  Flag,
  Globe2,
  GraduationCap,
  Hash,
  Languages,
  Mail,
  MapPin,
  Phone,
  Shield,
  StickyNote,
  User,
  Users,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

/* One field row, shared by the student, agency and admissions panels.

   All three used to define their own near-identical version with slightly
   different label widths, alignment and empty-value text, which is why the
   panels never quite matched. This is the single source of that layout. */

export const EMPTY_VALUE = '—';

// Labels that were written before this component existed carry no icon, so
// the common ones are matched by name. Anything unmatched gets a neutral dot,
// which keeps every row's text aligned on the same left edge.
const ICON_BY_LABEL = [
  [/date of birth|dob|\bdate\b|birthday/i, Calendar],
  [/e-?mail/i, Mail],
  [/phone|mobile|tel\b|whatsapp|wechat/i, Phone],
  [/citizenship|nationality|passport/i, Flag],
  [/country|status in canada|market|region/i, Globe2],
  [/address|city|province|state|postal|unit|street|apt/i, MapPin],
  [/program|course|study|intake|semester|campus/i, BookOpen],
  [/graduat|credential|diploma|certificate/i, GraduationCap],
  [/sin|insurance number|security/i, Shield],
  [/fee|tuition|payment|deposit|invoice|price|amount|commission/i, CreditCard],
  [/loan|bank|balance/i, Banknote],
  [/agency|company|organisation|organization|business/i, Building2],
  [/agent|contact|rep\b|owner|staff|person/i, User],
  [/note|remark|comment/i, StickyNote],
  [/number|\bid\b|reference|registration|gst/i, Hash],
  [/language|proficiency|english|french/i, Languages],
  [/student|applicant|linked/i, Users],
];

function iconForLabel(label) {
  const text = String(label || '');
  const match = ICON_BY_LABEL.find(([pattern]) => pattern.test(text));
  return match ? match[1] : CircleDot;
}

function isEmpty(value) {
  if (value === null || value === undefined || value === false) return true;
  if (typeof value === 'string') {
    const text = value.trim();
    // "Not set" was the student panel's old placeholder. Treating it as empty
    // stops the two conventions showing side by side during the changeover.
    return text === '' || text === 'Not set';
  }
  if (typeof value === 'number' || typeof value === 'boolean') return false;
  // Some fields pass markup rather than a string -- a legal name with the
  // preferred name beside it, for instance -- so nodes render as themselves.
  if (isValidElement(value) || Array.isArray(value)) return false;
  // A plain object would stringify to "[object Object]", so it reads as empty.
  return true;
}

function renderValue(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value;
}

/* Read-only field: icon, label, value. `value` and `children` are both
   accepted because the panels were written against different signatures. */
export function ProfileField({ icon, label, value, children, className, configured = false, style }) {
  const field = usePersonField(label);
  if (!configured && !field.visible) return null;
  if (!configured) { label = field.label; style = { ...style, order: field.order }; }
  const Icon = icon || iconForLabel(label);
  const hasChildren = children !== undefined && children !== null && children !== false;
  const body = hasChildren
    ? children
    : (isEmpty(value)
      ? <span className="font-normal text-[#062B1A]/40">{EMPTY_VALUE}</span>
      : renderValue(value));

  /* Label beside value from `sm` up, label above it below that. A phone is
     not wide enough for both: a fixed label column left about 150px for the
     value, which is not enough for an email address or a street. */
  return (
    <div style={style} className={cn('flex items-start gap-3 text-sm', className)}>
      <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#062B1A]/40" />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-start sm:gap-3">
        <span className="flex-shrink-0 text-[#062B1A]/60 sm:w-44">{label}</span>
        <span className="min-w-0 flex-1 break-words font-medium text-[#062B1A]">{body}</span>
      </div>
    </div>
  );
}

/* A field that reads as an icon row but becomes a labelled control in edit
   mode, where an input needs the full width of the row. */
export function EditableField({ icon, label, editing, display, children, full, className }) {
  const field = usePersonField(label);
  if (!field.visible) return null;
  label = field.label;
  if (!editing) {
    return (
      <ProfileField
        configured
        style={{ order: field.order }}
        icon={icon}
        label={label}
        value={display}
        className={cn(full ? 'sm:col-span-2' : '', className)}
      />
    );
  }
  return (
    <div style={{ order: field.order }} className={cn('space-y-1.5', full ? 'sm:col-span-2' : '', className)}>
      <Label className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
        {label}
      </Label>
      {children}
    </div>
  );
}

export default ProfileField;
